import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, Upload, Wand2, Sparkles, Save, X, ImagePlus, Loader2, AlertCircle, Check, Library as LibIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Pagina dedicata "Librerie / Archivi" per Casa Madre.
 * - Tab per ciascun asse (Tessuti, Gambe, Metalli, ...)
 * - Grid con anteprime + edit/delete inline
 * - 🤖 Scanner swatch AI: carica una tavola colori e Gemini estrae famiglia + voci
 * - Bulk grayscale: applica una stessa texture a tutti gli item filtrati
 */

const ALL_AXES = [
  { axis: "tessuti", label: "Tessuti", icona: "🧶", hasFabricType: true },
  { axis: "gambe", label: "Gambe", icona: "🦵" },
  { axis: "piedi", label: "Piedi (divani)", icona: "🦶" },
  { axis: "metalli", label: "Colori metallo", icona: "✨" },
  { axis: "piani", label: "Piani tavolo", icona: "🟫" },
  { axis: "basi", label: "Basi tavolo", icona: "🦴" },
  { axis: "finiture", label: "Finiture", icona: "🎨" },
  { axis: "maniglie", label: "Maniglie", icona: "🔘" },
  { axis: "dimensioni", label: "Dimensioni", icona: "📐" },
  { axis: "ante", label: "Ante", icona: "🚪" },
  { axis: "configurazioni", label: "Configurazioni divano", icona: "🛋️" },
  { axis: "giroletti", label: "Giroletti", icona: "🛏️" },
  { axis: "paralumi", label: "Paralumi", icona: "💡" },
  { axis: "cornici", label: "Cornici", icona: "🖼️" },
];

const EMPTY = {
  codice: "", nome: "", livello: "LVL1", sovrapprezzo: 0,
  hex_color: "#cccccc", immagine_url: "", famiglia: "",
  nome_colore: "", codice_colore: "", note: "",
  fabric_type: "fantasia", displacement_scale: 18, attivo: true,
};

const fmtEuro = (n) => "€ " + (Number(n) || 0).toFixed(2).replace(".", ",");

export default function CasaMadreLibrerie() {
  const [axis, setAxis] = useState("tessuti");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterLvl, setFilterLvl] = useState("");
  const [filterFabricType, setFilterFabricType] = useState("");

  const [editing, setEditing] = useState(null); // null = closed, EMPTY = create, item = edit
  const [scanResult, setScanResult] = useState(null); // {famiglia, items, ...}
  const [scanLoading, setScanLoading] = useState(false);
  const [forceExtract, setForceExtract] = useState(true); // default ON: estrai sempre la texture di ogni swatch
  const [stats, setStats] = useState({});
  const [families, setFamilies] = useState({}); // {famiglia: {id, cover_url, description, ordine}}
  const [editingFamily, setEditingFamily] = useState(null); // {famiglia, cover_url, description}

  const meta = ALL_AXES.find((a) => a.axis === axis) || ALL_AXES[0];

  const load = async () => {
    setLoading(true);
    try {
      const [list, axesRes, famRes] = await Promise.all([
        api.get(`/libreria/${axis}`),
        api.get("/libreria/axes"),
        api.get(`/libreria-famiglie/${axis}`),
      ]);
      setItems(list.data || []);
      const m = {};
      (axesRes.data || []).forEach((a) => { m[a.axis] = a; });
      setStats(m);
      const fm = {};
      (famRes.data || []).forEach((f) => { fm[f.famiglia] = f; });
      setFamilies(fm);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [axis]);

  const filtered = useMemo(() => {
    let arr = items;
    if (search) {
      const qs = search.toLowerCase();
      arr = arr.filter((i) =>
        (i.codice || "").toLowerCase().includes(qs) ||
        (i.nome || "").toLowerCase().includes(qs) ||
        (i.famiglia || "").toLowerCase().includes(qs) ||
        (i.nome_colore || "").toLowerCase().includes(qs));
    }
    if (filterLvl) arr = arr.filter((i) => i.livello === filterLvl);
    if (filterFabricType) arr = arr.filter((i) => (i.fabric_type || "fantasia") === filterFabricType);
    return arr;
  }, [items, search, filterLvl, filterFabricType]);

  const livelliDistinct = useMemo(() => Array.from(new Set(items.map((i) => i.livello).filter(Boolean))).sort(), [items]);

  // Raggruppa le voci filtrate per `famiglia`. Quelle senza famiglia vanno in "(Senza famiglia)".
  const groupedByFamily = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      const fam = (it.famiglia || "").toUpperCase().trim() || "_SENZA_FAMIGLIA_";
      if (!map.has(fam)) map.set(fam, []);
      map.get(fam).push(it);
    });
    // Ordina famiglie: prima quelle con cover, poi alfabetiche, "_SENZA_FAMIGLIA_" sempre ultima
    const entries = Array.from(map.entries()).sort((a, b) => {
      if (a[0] === "_SENZA_FAMIGLIA_") return 1;
      if (b[0] === "_SENZA_FAMIGLIA_") return -1;
      const oa = families[a[0]]?.ordine ?? 999;
      const ob = families[b[0]]?.ordine ?? 999;
      if (oa !== ob) return oa - ob;
      return a[0].localeCompare(b[0]);
    });
    return entries;
  }, [filtered, families]);

  // ─── Cover famiglia ───
  const openFamilyCover = (famiglia) => {
    const existing = families[famiglia] || {};
    setEditingFamily({ famiglia, cover_url: existing.cover_url || "", description: existing.description || "" });
  };
  const uploadFamilyCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditingFamily((e) => ({ ...e, cover_url: r.data.url }));
      toast.success("Cover caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    }
  };
  const saveFamilyCover = async () => {
    if (!editingFamily) return;
    try {
      const r = await api.put(`/libreria-famiglie/${axis}/${editingFamily.famiglia}`, {
        cover_url: editingFamily.cover_url || "",
        description: editingFamily.description || "",
      });
      setFamilies((fs) => ({ ...fs, [editingFamily.famiglia]: r.data }));
      toast.success(`Cover di "${editingFamily.famiglia}" aggiornata`);
      setEditingFamily(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };
  const removeFamilyCover = async () => {
    if (!editingFamily) return;
    if (!window.confirm(`Rimuovere la copertina di "${editingFamily.famiglia}"?`)) return;
    try {
      await api.delete(`/libreria-famiglie/${axis}/${editingFamily.famiglia}`);
      setFamilies((fs) => { const c = { ...fs }; delete c[editingFamily.famiglia]; return c; });
      toast.success(`Cover di "${editingFamily.famiglia}" rimossa`);
      setEditingFamily(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  // ─── CRUD ───
  const openCreate = () => setEditing({ ...EMPTY });
  const openEdit = (it) => setEditing({
    ...EMPTY,
    ...it,
    displacement_scale: it.meta?.displacement_scale ?? 18,
    hex_color: it.hex_color || "#cccccc",
  });
  const closeEditor = () => setEditing(null);

  const uploadField = async (file, field = "immagine_url") => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, [field]: r.data.url }));
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    }
  };

  const save = async () => {
    if (!editing.codice?.trim()) { toast.error("Codice obbligatorio"); return; }
    const payload = {
      nome: editing.nome || editing.codice,
      livello: editing.livello || "LVL1",
      sovrapprezzo: parseFloat(editing.sovrapprezzo) || 0,
      hex_color: editing.hex_color || "",
      immagine_url: editing.immagine_url || "",
      nome_colore: editing.nome_colore || "",
      codice_colore: editing.codice_colore || "",
      note: editing.note || "",
      famiglia: editing.famiglia || "",
      attivo: editing.attivo !== false,
      meta: {},
    };
    if (meta.hasFabricType) {
      payload.fabric_type = editing.fabric_type;
      if (editing.fabric_type === "materico") {
        payload.meta.displacement_scale = parseFloat(editing.displacement_scale) || 18;
      }
    }
    try {
      if (editing.id) {
        const r = await api.put(`/libreria/${axis}/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => x.id === r.data.id ? r.data : x));
        toast.success(`"${r.data.codice}" aggiornato`);
      } else {
        payload.codice = editing.codice.trim().toUpperCase();
        const r = await api.post(`/libreria/${axis}`, payload);
        setItems((arr) => [...arr, r.data]);
        toast.success(`"${r.data.codice}" creato`);
      }
      closeEditor();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it.codice}" dalla libreria?`)) return;
    try {
      await api.delete(`/libreria/${axis}/${it.id}`);
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success(`"${it.codice}" eliminato`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  // ─── Scanner swatch AI ───
  const startScan = async (file) => {
    if (!file) return;
    setScanLoading(true);
    setScanResult(null);
    toast.info("L'AI sta analizzando la tavola colori... (30-60 sec)");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("axis", axis);
    fd.append("force_extract", forceExtract ? "true" : "false");
    try {
      const r = await api.post("/libreria-scanner/swatch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Stato locale per ogni item: include/exclude + override codice/hex
      const enriched = (r.data.items || []).map((it) => ({
        ...it,
        _include: true,
        _on_duplicate: it.duplicate_of ? "skip" : "create", // skip|overwrite|create
      }));
      setScanResult({ ...r.data, items: enriched });
      if (!enriched.length) toast.warning("L'AI non ha riconosciuto voci. Riprova con un'immagine più nitida.");
      else toast.success(`L'AI ha trovato ${enriched.length} voci`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore scanner AI");
    } finally { setScanLoading(false); }
  };

  const confirmScanImport = async () => {
    if (!scanResult) return;
    const toImport = scanResult.items.filter((it) => it._include);
    // Determina on_duplicate globale dalle scelte: se tutti uguali, usa quello, sennò "skip" come default
    const decisions = new Set(toImport.map((it) => it._on_duplicate));
    const globalDup = decisions.size === 1 ? [...decisions][0] : "skip";
    const onDup = globalDup === "skip" ? "skip" : globalDup === "overwrite" ? "overwrite" : "skip";
    try {
      const r = await api.post(`/libreria/${axis}/bulk-import`, {
        items: toImport.map((it) => ({
          codice: it.codice,
          nome: it.nome,
          hex_color: it.hex_color,
          codice_colore: it.codice_colore,
          livello: it.livello,
          sovrapprezzo: it.sovrapprezzo,
          immagine_url: it.immagine_url,
          fabric_type: it.fabric_type,
          famiglia: it.famiglia,
          meta: {},
        })),
        on_duplicate: onDup,
      });
      toast.success(`Importati: ${r.data.inserted} nuovi, ${r.data.updated} aggiornati, ${r.data.skipped} saltati`);
      setScanResult(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore import");
    }
  };

  return (
    <AdminLayout title="Librerie / Archivi">
      <div className="max-w-[1500px] mx-auto p-6 lg:p-10">
        <div className="mb-6">
          <div className="overline text-brand mb-1">Casa Madre · Archivi</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">Librerie</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-3xl">
            Gestisci le opzioni configurabili: tessuti, gambe, metalli e tutti gli altri assi.
            Aggiungi voci manualmente, modificale, oppure usa lo <strong>Scanner AI</strong> per estrarre automaticamente
            famiglia e colori da una foto di una tavola campionario.
          </p>
        </div>

        {/* Tab axis selector */}
        <div className="flex flex-wrap gap-1.5 mb-6 border-b border-border pb-2" data-testid="axis-tabs">
          {ALL_AXES.map((a) => {
            const active = axis === a.axis;
            const n = stats[a.axis]?.attivi || 0;
            return (
              <button key={a.axis} onClick={() => { setAxis(a.axis); setSearch(""); setFilterLvl(""); setFilterFabricType(""); }}
                data-testid={`tab-axis-${a.axis}`}
                className={`px-3 py-2 rounded-sm text-xs font-medium transition-all inline-flex items-center gap-1.5 ${active ? "bg-foreground text-white shadow-sm" : "bg-white border border-border hover:border-foreground"}`}>
                <span>{a.icona}</span>
                <span>{a.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? "bg-white/20" : "bg-accent/40"}`}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-border rounded-sm p-4 mb-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={`Cerca in ${meta.label.toLowerCase()}…`}
              className="pl-9 h-10 rounded-sm" data-testid="lib-search" />
          </div>
          {livelliDistinct.length > 0 && (
            <select value={filterLvl} onChange={(e) => setFilterLvl(e.target.value)}
              data-testid="lib-filter-lvl"
              className="h-10 px-3 rounded-sm border border-border bg-white text-sm">
              <option value="">Tutti i livelli</option>
              {livelliDistinct.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          {meta.hasFabricType && (
            <select value={filterFabricType} onChange={(e) => setFilterFabricType(e.target.value)}
              data-testid="lib-filter-type"
              className="h-10 px-3 rounded-sm border border-border bg-white text-sm">
              <option value="">Tutti i tipi</option>
              <option value="tinta_unita">Tinta unita</option>
              <option value="fantasia">Fantasia</option>
              <option value="materico">Materico</option>
            </select>
          )}
          <button onClick={openCreate} data-testid="lib-btn-create"
            className="h-10 px-4 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Nuova voce
          </button>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" title="Default: l'AI ritaglia la texture di ogni swatch e la salva in archivio. Disattiva per usare solo la grayscale macro condivisa (più rapida ma meno fedele).">
            <input type="checkbox" checked={forceExtract} onChange={(e) => setForceExtract(e.target.checked)} data-testid="lib-force-extract" />
            Estrai texture di ogni swatch
          </label>
          <label className="h-10 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 cursor-pointer" data-testid="lib-btn-scan">
            <Sparkles className="w-4 h-4" /> {scanLoading ? "Analisi…" : "Scanner AI"}
            <input type="file" accept="image/*" className="hidden" disabled={scanLoading}
              onChange={(e) => startScan(e.target.files?.[0])} />
          </label>
        </div>

        {/* Risultati */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="text-xs text-muted-foreground mb-3">{filtered.length} di {items.length} voci</div>
        )}

        <div className="space-y-6" data-testid="lib-grouped">
          {groupedByFamily.map(([fam, famItems]) => {
            const isOrphan = fam === "_SENZA_FAMIGLIA_";
            const famMeta = families[fam] || {};
            const coverUrl = famMeta.cover_url;
            const famLabel = isOrphan ? "(Senza famiglia)" : fam;
            return (
              <div key={fam} data-testid={`family-block-${fam}`}>
                {/* Header famiglia con cover */}
                <div className="relative bg-white border border-border rounded-sm overflow-hidden mb-3 group">
                  <div className="flex items-stretch">
                    {/* Cover image / placeholder */}
                    <div className="w-32 sm:w-44 h-24 sm:h-28 flex-shrink-0 bg-neutral-100 relative overflow-hidden">
                      {coverUrl ? (
                        <img src={coverUrl} alt={famLabel} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-muted-foreground/40">
                          {isOrphan ? "?" : fam.slice(0, 2)}
                        </div>
                      )}
                      {!isOrphan && (
                        <button onClick={() => openFamilyCover(fam)} data-testid={`family-cover-edit-${fam}`}
                          title={coverUrl ? "Modifica copertina" : "Aggiungi copertina"}
                          className="absolute inset-0 bg-black/0 hover:bg-black/40 text-white opacity-0 hover:opacity-100 transition-all inline-flex items-center justify-center gap-1 text-xs font-medium">
                          <ImagePlus className="w-4 h-4" /> {coverUrl ? "Modifica cover" : "Aggiungi cover"}
                        </button>
                      )}
                    </div>
                    {/* Info famiglia */}
                    <div className="flex-1 p-3 sm:p-4 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <h3 className="font-heading font-bold text-xl sm:text-2xl tracking-tight">{famLabel}</h3>
                        <span className="text-xs text-muted-foreground font-mono">{famItems.length} {famItems.length === 1 ? "voce" : "voci"}</span>
                      </div>
                      {famMeta.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 max-w-2xl">{famMeta.description}</p>
                      )}
                      {!famMeta.description && !isOrphan && (
                        <p className="text-xs text-muted-foreground/60 italic mt-1">Nessuna descrizione · clicca la cover per aggiungerne una</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Grid voci della famiglia */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
                  {famItems.map((it) => {
                    const isTinta = it.fabric_type === "tinta_unita";
                    return (
                      <div key={it.id} className={`bg-white border rounded-sm overflow-hidden transition-all hover:shadow-md group ${it.attivo ? "border-border" : "border-border opacity-50"}`} data-testid={`lib-item-${it.codice}`}>
                        <div className="aspect-square bg-neutral-100 relative overflow-hidden">
                          {isTinta && it.hex_color ? (
                            <div className="absolute inset-0" style={{ background: it.hex_color }} />
                          ) : it.immagine_url ? (
                            <img src={it.immagine_url} alt={it.codice} className="w-full h-full object-cover" />
                          ) : it.hex_color ? (
                            <div className="absolute inset-0" style={{ background: it.hex_color }} />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-muted-foreground">{it.codice.slice(0, 2)}</div>
                          )}
                          {isTinta && it.immagine_url && (
                            <svg className="absolute inset-0 w-full h-full opacity-90" preserveAspectRatio="xMidYMid slice">
                              <defs>
                                <filter id={`tint-${it.id}`}>
                                  <feColorMatrix type="matrix" values={`${parseInt((it.hex_color||"#cccccc").slice(1,3),16)/255} 0 0 0 0  ${parseInt((it.hex_color||"#cccccc").slice(3,5),16)/255} 0 0 0 0  ${parseInt((it.hex_color||"#cccccc").slice(5,7),16)/255} 0 0 0 0  0 0 0 1 0`} />
                                </filter>
                              </defs>
                              <image href={it.immagine_url} width="100%" height="100%" preserveAspectRatio="xMidYMid slice" filter={`url(#tint-${it.id})`} />
                            </svg>
                          )}
                          {it.fabric_type && it.fabric_type !== "fantasia" && (
                            <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${it.fabric_type === "tinta_unita" ? "bg-blue-600 text-white" : "bg-amber-600 text-white"}`}>
                              {it.fabric_type === "tinta_unita" ? "Tinta" : "Materico"}
                            </span>
                          )}
                          {it.livello && (
                            <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px] font-bold">{it.livello}</span>
                          )}
                          <div className="absolute inset-x-0 bottom-0 flex bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEdit(it)} data-testid={`lib-edit-${it.codice}`}
                              className="flex-1 h-7 rounded-sm bg-white text-foreground text-[11px] font-bold hover:bg-brand hover:text-white inline-flex items-center justify-center gap-1">
                              <Pencil className="w-3 h-3" /> Modifica
                            </button>
                            <button onClick={() => remove(it)} data-testid={`lib-delete-${it.codice}`}
                              className="ml-1 w-7 h-7 rounded-sm bg-white text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center justify-center">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="p-2">
                          <div className="font-bold text-xs truncate" title={it.nome}>{it.nome || it.codice}</div>
                          <div className="text-[10px] text-muted-foreground font-mono truncate">{it.codice}</div>
                          <div className="flex items-center justify-between mt-1">
                            {it.hex_color && (
                              <div className="flex items-center gap-1 text-[10px]">
                                <div className="w-3 h-3 rounded-sm border border-border" style={{ background: it.hex_color }} />
                                <span className="font-mono">{it.hex_color}</span>
                              </div>
                            )}
                            {it.sovrapprezzo > 0 && (
                              <span className="text-[10px] font-bold text-brand">+ {fmtEuro(it.sovrapprezzo)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-dashed border-border rounded-sm p-12 text-center text-muted-foreground">
            <LibIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="mb-3">Nessuna voce trovata in <strong>{meta.label}</strong>.</p>
            <div className="flex gap-2 justify-center">
              <button onClick={openCreate} className="h-9 px-4 rounded-sm bg-foreground text-white text-sm hover:bg-brand inline-flex items-center gap-1.5">
                <Plus className="w-4 h-4" /> Aggiungi manualmente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing?.id ? <><Pencil className="w-4 h-4" /> Modifica voce</> : <><Plus className="w-4 h-4" /> Nuova voce {meta.label}</>}
            </DialogTitle>
            <DialogDescription>
              {editing?.id ? `Stai modificando "${editing.codice}"` : `Aggiungi una nuova voce alla libreria "${meta.label}"`}
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              {meta.hasFabricType && (
                <div>
                  <label className="block text-xs font-semibold mb-1">Tipo tessuto</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "tinta_unita", t: "Tinta unita", d: "Grayscale + HEX dinamico" },
                      { v: "fantasia", t: "Fantasia", d: "Texture stampata a colori" },
                      { v: "materico", t: "Materico", d: "Effetto rilievo 3D" },
                    ].map((opt) => (
                      <button key={opt.v} type="button" onClick={() => setEditing((e) => ({ ...e, fabric_type: opt.v }))}
                        data-testid={`edit-fabric-${opt.v}`}
                        className={`text-left p-2 rounded-sm border text-xs transition-all ${editing.fabric_type === opt.v ? "border-brand bg-brand/10 ring-1 ring-brand/30" : "border-border hover:border-foreground bg-white"}`}>
                        <div className="font-bold">{opt.t}</div>
                        <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{opt.d}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Codice <span className="text-red-600">*</span></label>
                  <Input value={editing.codice} onChange={(e) => setEditing((s) => ({ ...s, codice: e.target.value }))}
                    placeholder="es. BARREL-19" className="h-9 rounded-sm" disabled={!!editing.id} data-testid="edit-codice" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Nome / Etichetta</label>
                  <Input value={editing.nome} onChange={(e) => setEditing((s) => ({ ...s, nome: e.target.value }))}
                    placeholder="es. Barrel 19 Tortora" className="h-9 rounded-sm" data-testid="edit-nome" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Famiglia</label>
                  <Input value={editing.famiglia} onChange={(e) => setEditing((s) => ({ ...s, famiglia: e.target.value }))}
                    placeholder="BARREL" className="h-9 rounded-sm font-mono" data-testid="edit-famiglia" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Codice colore</label>
                  <Input value={editing.codice_colore} onChange={(e) => setEditing((s) => ({ ...s, codice_colore: e.target.value }))}
                    placeholder="19" className="h-9 rounded-sm" data-testid="edit-codcolore" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Nome colore</label>
                  <Input value={editing.nome_colore} onChange={(e) => setEditing((s) => ({ ...s, nome_colore: e.target.value }))}
                    placeholder="Tortora" className="h-9 rounded-sm" data-testid="edit-nomecol" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Livello prezzo</label>
                  <select value={editing.livello} onChange={(e) => setEditing((s) => ({ ...s, livello: e.target.value }))}
                    className="w-full h-9 rounded-sm border border-input bg-white px-2 text-sm" data-testid="edit-livello">
                    {["LVL1", "LVL2", "LVL3", "LVL4", "LVL5"].map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Sovrapprezzo globale €</label>
                  <Input type="number" step="0.10" value={editing.sovrapprezzo}
                    onChange={(e) => setEditing((s) => ({ ...s, sovrapprezzo: e.target.value }))}
                    className="h-9 rounded-sm" data-testid="edit-sovra" />
                  <div className="text-[10px] text-muted-foreground mt-0.5">Default per tutti i modelli. Override per prodotto nel Configuratore.</div>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Colore HEX</label>
                  <div className="flex gap-1">
                    <input type="color" value={editing.hex_color || "#cccccc"}
                      onChange={(e) => setEditing((s) => ({ ...s, hex_color: e.target.value }))}
                      className="h-9 w-12 rounded-sm border border-input cursor-pointer" data-testid="edit-hexpicker" />
                    <Input value={editing.hex_color || ""} onChange={(e) => setEditing((s) => ({ ...s, hex_color: e.target.value }))}
                      className="h-9 rounded-sm font-mono text-xs" data-testid="edit-hextext" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">
                  Immagine texture {meta.hasFabricType && editing.fabric_type === "tinta_unita" && <span className="text-muted-foreground">(grayscale consigliata)</span>}
                </label>
                <div className="flex gap-3 items-start">
                  <div className="flex-1 space-y-2">
                    <input type="file" accept="image/*" onChange={(e) => uploadField(e.target.files?.[0])}
                      className="text-xs w-full" data-testid="edit-imgfile" />
                    <Input value={editing.immagine_url} onChange={(e) => setEditing((s) => ({ ...s, immagine_url: e.target.value }))}
                      placeholder="URL immagine" className="h-8 rounded-sm font-mono text-[10px]" data-testid="edit-imgurl" />
                  </div>
                  {editing.immagine_url && (
                    <div className="relative">
                      {meta.hasFabricType && editing.fabric_type === "tinta_unita" ? (
                        <svg width="80" height="80" className="rounded-sm border border-border">
                          <defs>
                            <filter id="prevTintLib">
                              <feColorMatrix type="matrix" values={`${parseInt((editing.hex_color || "#cccccc").slice(1,3),16)/255} 0 0 0 0  ${parseInt((editing.hex_color || "#cccccc").slice(3,5),16)/255} 0 0 0 0  ${parseInt((editing.hex_color || "#cccccc").slice(5,7),16)/255} 0 0 0 0  0 0 0 1 0`} />
                            </filter>
                          </defs>
                          <image href={editing.immagine_url} width="80" height="80" preserveAspectRatio="xMidYMid slice" filter="url(#prevTintLib)" />
                        </svg>
                      ) : (
                        <img src={editing.immagine_url} alt="preview" className="w-20 h-20 rounded-sm object-cover border border-border" />
                      )}
                      <button onClick={() => setEditing((s) => ({ ...s, immagine_url: "" }))} type="button"
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-white rounded-full text-xs flex items-center justify-center">×</button>
                    </div>
                  )}
                </div>
              </div>

              {meta.hasFabricType && editing.fabric_type === "materico" && (
                <div>
                  <label className="block text-xs font-semibold mb-1">Intensità rilievo (0-40)</label>
                  <input type="range" min="0" max="40" step="1" value={editing.displacement_scale}
                    onChange={(e) => setEditing((s) => ({ ...s, displacement_scale: e.target.value }))}
                    className="w-full" data-testid="edit-disp" />
                  <div className="text-[10px] text-muted-foreground text-right">{editing.displacement_scale}</div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold mb-1">Note interne</label>
                <textarea value={editing.note} onChange={(e) => setEditing((s) => ({ ...s, note: e.target.value }))}
                  rows="2" placeholder="Note interne (composizione, fornitore, lavaggio…)"
                  className="w-full text-xs rounded-sm border border-input px-3 py-2 bg-white" data-testid="edit-note" />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editing.attivo !== false} onChange={(e) => setEditing((s) => ({ ...s, attivo: e.target.checked }))} id="edit-attivo" data-testid="edit-attivo" />
                <label htmlFor="edit-attivo" className="text-xs cursor-pointer">Voce attiva (visibile nel configuratore B2C)</label>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-border">
                <button onClick={closeEditor} className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent">Annulla</button>
                <button onClick={save} data-testid="edit-save"
                  className="h-10 px-5 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand inline-flex items-center gap-1.5">
                  <Save className="w-4 h-4" /> {editing.id ? "Salva modifiche" : "Crea voce"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Editor cover famiglia */}
      <Dialog open={!!editingFamily} onOpenChange={(o) => !o && setEditingFamily(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus className="w-4 h-4" /> Copertina famiglia: {editingFamily?.famiglia}
            </DialogTitle>
            <DialogDescription>
              Imposta l&apos;immagine di copertina e una breve descrizione per la famiglia. La copertina apparirà come hero del gruppo nella libreria.
            </DialogDescription>
          </DialogHeader>
          {editingFamily && (
            <div className="space-y-4">
              {/* Anteprima cover attuale */}
              <div className="relative aspect-[16/9] bg-neutral-100 rounded-sm overflow-hidden border border-border">
                {editingFamily.cover_url ? (
                  <img src={editingFamily.cover_url} alt="cover" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-muted-foreground/40">
                    {editingFamily.famiglia.slice(0, 2)}
                  </div>
                )}
              </div>
              {/* Upload */}
              <div>
                <label className="block text-xs font-semibold mb-1">Carica immagine</label>
                <input type="file" accept="image/*" onChange={(e) => uploadFamilyCover(e.target.files?.[0])}
                  className="text-xs w-full" data-testid="family-cover-file" />
                <Input value={editingFamily.cover_url || ""} onChange={(e) => setEditingFamily((s) => ({ ...s, cover_url: e.target.value }))}
                  placeholder="oppure incolla URL" className="mt-2 h-9 rounded-sm font-mono text-xs" data-testid="family-cover-url" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Descrizione famiglia (opzionale)</label>
                <textarea value={editingFamily.description || ""}
                  onChange={(e) => setEditingFamily((s) => ({ ...s, description: e.target.value }))}
                  rows="3" placeholder="Es. Tessuto in lana 100% naturale, lavabile a secco, disponibile in 50 varianti colore."
                  className="w-full text-xs rounded-sm border border-input px-3 py-2 bg-white" data-testid="family-cover-desc" />
              </div>
              <div className="flex justify-between gap-2 pt-3 border-t border-border">
                <button onClick={removeFamilyCover} disabled={!families[editingFamily.famiglia]}
                  className="h-10 px-4 rounded-sm border border-red-300 text-red-700 text-sm hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
                  <Trash2 className="w-4 h-4" /> Rimuovi
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setEditingFamily(null)} className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent">Annulla</button>
                  <button onClick={saveFamilyCover} data-testid="family-cover-save"
                    className="h-10 px-5 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand inline-flex items-center gap-1.5">
                    <Save className="w-4 h-4" /> Salva
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scanner AI: dialog di anteprima/conferma */}
      <Dialog open={!!scanResult} onOpenChange={(o) => !o && setScanResult(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand" /> Anteprima scansione AI
            </DialogTitle>
            <DialogDescription>
              {scanResult && <>L&apos;AI ha rilevato la famiglia <strong>{scanResult.famiglia || "—"}</strong>, tipo suggerito <strong>{scanResult.fabric_type_suggested || "fantasia"}</strong>, con {scanResult.items?.length || 0} voci{scanResult.duplicates_count ? `, di cui ${scanResult.duplicates_count} duplicati già esistenti` : ""}. Controlla, modifica e conferma.</>}
            </DialogDescription>
          </DialogHeader>

          {scanResult && (
            <div className="space-y-4">
              {/* Macro grayscale ritagliata */}
              {scanResult.macro_url && (
                <div className="bg-amber-50 border border-amber-300 rounded-sm p-3 flex items-center gap-3">
                  <img src={scanResult.macro_url} alt="grayscale base" className="w-20 h-20 rounded-sm object-cover border border-amber-400" />
                  <div className="flex-1">
                    <div className="text-xs font-bold text-amber-900">Texture macro grayscale estratta</div>
                    <div className="text-[11px] text-amber-800">Verrà applicata a tutte le voci marcate come <em>tinta unita</em>. Puoi modificarla individualmente dopo l&apos;import.</div>
                  </div>
                </div>
              )}

              {/* Bulk actions */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Azione duplicati:</span>
                <button onClick={() => setScanResult((s) => ({ ...s, items: s.items.map((it) => it.duplicate_of ? { ...it, _on_duplicate: "skip" } : it) }))}
                  className="px-2 py-1 rounded-sm border border-border hover:bg-accent">Salta tutti i duplicati</button>
                <button onClick={() => setScanResult((s) => ({ ...s, items: s.items.map((it) => it.duplicate_of ? { ...it, _on_duplicate: "overwrite" } : it) }))}
                  className="px-2 py-1 rounded-sm border border-border hover:bg-accent">Sovrascrivi tutti</button>
                <span className="ml-auto text-muted-foreground">{scanResult.items.filter((it) => it._include).length} di {scanResult.items.length} selezionate</span>
              </div>

              {/* Lista item proposti */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[55vh] overflow-y-auto pr-1">
                {scanResult.items.map((it, idx) => {
                  const isDup = !!it.duplicate_of;
                  return (
                    <div key={idx} data-testid={`scan-item-${it.codice}`}
                      className={`border rounded-sm p-2 flex gap-2 ${it._include ? "border-brand/40 bg-white" : "border-border bg-neutral-50 opacity-60"} ${isDup ? "ring-1 ring-amber-300" : ""}`}>
                      <input type="checkbox" checked={it._include}
                        onChange={(e) => setScanResult((s) => ({ ...s, items: s.items.map((x, i) => i === idx ? { ...x, _include: e.target.checked } : x) }))}
                        className="mt-1 flex-shrink-0" data-testid={`scan-include-${it.codice}`} />
                      <div className="w-12 h-12 rounded-sm border border-border flex-shrink-0" style={{ background: it.hex_color || "#ccc" }} />
                      <div className="flex-1 min-w-0 text-[11px]">
                        <div className="font-bold truncate">{it.nome}</div>
                        <div className="font-mono text-muted-foreground truncate">{it.codice}</div>
                        <div className="flex items-center gap-1 text-[10px] mt-0.5">
                          <span className="font-mono">{it.hex_color}</span>
                          {it.sovrapprezzo > 0 && <span>· +€{it.sovrapprezzo}</span>}
                          {it.livello && <span>· {it.livello}</span>}
                        </div>
                        {isDup && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] bg-amber-50 border border-amber-300 rounded px-1 py-0.5">
                            <AlertCircle className="w-3 h-3 text-amber-700 flex-shrink-0" />
                            <div className="flex-1 truncate text-amber-900">Esiste già</div>
                            <select value={it._on_duplicate} onChange={(e) => setScanResult((s) => ({ ...s, items: s.items.map((x, i) => i === idx ? { ...x, _on_duplicate: e.target.value } : x) }))}
                              className="text-[10px] rounded border border-amber-300 bg-white px-0.5">
                              <option value="skip">Salta</option>
                              <option value="overwrite">Sovrascrivi</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-border">
                <button onClick={() => setScanResult(null)} className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent">Annulla</button>
                <button onClick={confirmScanImport} data-testid="scan-confirm"
                  className="h-10 px-5 rounded-sm bg-brand text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4" /> Importa {scanResult.items.filter((it) => it._include).length} voci
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {scanLoading && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-sm p-8 max-w-md text-center">
            <Loader2 className="w-10 h-10 animate-spin text-brand mx-auto mb-3" />
            <div className="font-bold mb-1">Analisi AI in corso…</div>
            <div className="text-xs text-muted-foreground">Gemini sta leggendo la tavola, estraendo HEX e identificando la famiglia. Operazione: 30-60 secondi.</div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
